import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Paper,
  Chip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Search as SearchIcon,
  LocationOn,
  Phone,
  Email,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import AddBranchDialog from './AddBranchDialog';
import EditBranchDialog from './EditBranchDialog';
import BranchUserManagement from './BranchUserManagement';
import PageContainer from '../shared/PageContainer';

// Color palette for branch avatars
const branchColors = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
  '#c62828', '#00838f', '#4e342e', '#283593'
];

const getBranchColor = (name) => {
  const index = name.charCodeAt(0) % branchColors.length;
  return branchColors[index];
};

const BranchManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedBranchForUsers, setSelectedBranchForUsers] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBranches();
  }, []);

  // Auto-open add dialog if no branches exist or if coming from dashboard
  useEffect(() => {
    const shouldOpenDialog = location.state?.openAddDialog || (!loading && branches.length === 0);
    if (shouldOpenDialog) {
      setAddDialogOpen(true);
    }
  }, [loading, branches.length, location.state]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data.branches);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load branches');
      setLoading(false);
    }
  };

  const handleAddSuccess = async (newBranch) => {
    // Fetch fresh data to ensure we have complete branch information
    await fetchBranches();

    // If this was the first branch, redirect to dashboard
    if (branches.length === 0) {
      navigate('/app/dashboard');
    }
  };

  const handleEditSuccess = (updatedBranch) => {
    setBranches(prev =>
      prev.map(branch =>
        branch.id === updatedBranch.id ? updatedBranch : branch
      )
    );
  };

  const filteredBranches = branches.filter(branch => {
    const searchLower = searchQuery.toLowerCase();
    return (
      branch.name.toLowerCase().includes(searchLower) ||
      branch.address.toLowerCase().includes(searchLower) ||
      branch.branch_code.toLowerCase().includes(searchLower) ||
      branch.email?.toLowerCase().includes(searchLower) ||
      branch.phone?.toLowerCase().includes(searchLower)
    );
  });

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const renderGridView = () => (
    <Grid container spacing={3}>
      {filteredBranches.map(branch => {
        const color = getBranchColor(branch.name);
        return (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={branch.id}>
            <Card
              elevation={0}
              onClick={() => navigate(`/app/branches/${branch.id}`)}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(color, 0.02),
                opacity: branch.status === 'inactive' ? 0.7 : 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
                  borderColor: alpha(color, 0.3),
                }
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBranch(branch);
                  setEditDialogOpen(true);
                }}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.primary, 0.1)
                  }
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>

              <CardContent sx={{ flex: 1, p: 3 }}>
                {/* Header: Name + Badges */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                      {branch.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {branch.is_main_branch && (
                        <Chip
                          label="Main Branch"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          }}
                        />
                      )}
                      <Chip
                        icon={branch.status === 'active'
                          ? <ActiveIcon sx={{ fontSize: '14px !important' }} />
                          : <InactiveIcon sx={{ fontSize: '14px !important' }} />
                        }
                        label={branch.status || 'active'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          bgcolor: branch.status === 'active'
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          color: branch.status === 'active' ? 'success.main' : 'error.main',
                          border: `1px solid ${
                            branch.status === 'active'
                              ? alpha(theme.palette.success.main, 0.25)
                              : alpha(theme.palette.error.main, 0.25)
                          }`,
                          '& .MuiChip-icon': {
                            color: branch.status === 'active' ? 'success.main' : 'error.main',
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <LocationOn sx={{ fontSize: 18, color: 'text.disabled', mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {branch.address}
                    </Typography>
                  </Box>
                  {branch.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Phone sx={{ fontSize: 18, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        {branch.phone}
                      </Typography>
                    </Box>
                  )}
                  {branch.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Email sx={{ fontSize: 18, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        {branch.email}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <StoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {branch.branch_code}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>

              {/* Footer */}
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  mt: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  startIcon={<PeopleIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBranchForUsers(branch);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: color,
                    '&:hover': {
                      bgcolor: alpha(color, 0.08),
                    }
                  }}
                >
                  Manage Users
                </Button>
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderListView = () => (
    <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <List disablePadding>
        {filteredBranches.map((branch, index) => {
          const color = getBranchColor(branch.name);
          return (
            <ListItem
              key={branch.id}
              onClick={() => navigate(`/app/branches/${branch.id}`)}
              sx={{
                cursor: 'pointer',
                opacity: branch.status === 'inactive' ? 0.7 : 1,
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  bgcolor: alpha(color, 0.04),
                },
                borderBottom: index < filteredBranches.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                py: 2,
                px: 3,
                gap: 2,
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {branch.name}
                    </Typography>
                    {branch.is_main_branch && (
                      <Chip
                        label="Main Branch"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                        }}
                      />
                    )}
                    <Chip
                      icon={branch.status === 'active'
                        ? <ActiveIcon sx={{ fontSize: '14px !important' }} />
                        : <InactiveIcon sx={{ fontSize: '14px !important' }} />
                      }
                      label={branch.status || 'active'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        bgcolor: branch.status === 'active'
                          ? alpha(theme.palette.success.main, 0.1)
                          : alpha(theme.palette.error.main, 0.1),
                        color: branch.status === 'active' ? 'success.main' : 'error.main',
                        border: `1px solid ${
                          branch.status === 'active'
                            ? alpha(theme.palette.success.main, 0.25)
                            : alpha(theme.palette.error.main, 0.25)
                        }`,
                        '& .MuiChip-icon': {
                          color: branch.status === 'active' ? 'success.main' : 'error.main',
                        }
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 3, mt: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        {branch.address}
                      </Typography>
                    </Box>
                    {branch.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">
                          {branch.email}
                        </Typography>
                      </Box>
                    )}
                    {branch.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">
                          {branch.phone}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {branch.branch_code}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBranch(branch);
                  setEditDialogOpen(true);
                }}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.primary, 0.1)
                  }
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );

  if (loading) {
    return (
      <PageContainer title="Branch Management">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={branches.length === 0 ? 'Create Your First Branch' : 'Branch Management'}
      subtitle={branches.length > 0 ? 'Manage your business locations' : undefined}
      actions={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {branches.length > 0 && (
            <>
              <TextField
                placeholder="Search branches..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ width: 250 }}
              />
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Branch
          </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {branches.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <StoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Branches Yet
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
          >
            To get started with your POS system, you'll need to create at least one branch.
            This will be your main branch where you can manage your inventory, sales, and staff.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Create Your First Branch
          </Button>
        </Box>
      ) : (
        viewMode === 'grid' ? renderGridView() : renderListView()
      )}

      <AddBranchDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
        isFirstBranch={branches.length === 0}
      />

      <EditBranchDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedBranch(null);
        }}
        onSuccess={handleEditSuccess}
        branch={selectedBranch}
      />

      {/* User Management Dialog */}
      <Dialog
        open={Boolean(selectedBranchForUsers)}
        onClose={() => setSelectedBranchForUsers(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedBranchForUsers && (
            <BranchUserManagement
              branchId={selectedBranchForUsers.id}
              branchName={selectedBranchForUsers.name}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default BranchManagement;
